import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, CATEGORIES, errMsg } from "@/lib/api";
import { Container, SectionLabel } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Rocket, Loader2, AlertCircle, ShieldCheck, ArrowRight } from "lucide-react";

export default function StartFundraiser() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: "", category: "Medical", goal_amount: "", is_nonprofit: false, is_team: false });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/campaigns", {
        title: form.title,
        category: form.category,
        goal_amount: form.goal_amount ? Number(form.goal_amount) : null,
        is_nonprofit: form.is_nonprofit,
        is_team: form.is_team,
      });
      navigate(`/organizer/campaign-builder/${data.id}`);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#F3F4F6]">
      <Container className="grid gap-10 py-14 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <SectionLabel>Start a fundraiser</SectionLabel>
          <h1 className="mt-2 font-heading text-4xl font-extrabold leading-tight tracking-tight text-ff-blue sm:text-5xl">
            It's free to start. Reviewed before you go live.
          </h1>
          <p className="mt-5 max-w-lg text-ff-dark">
            Create your campaign in minutes. Complete organizer and payout onboarding, then submit for
            review. FightFund reviews campaigns and payout requests before funds are released.
          </p>
          <ul className="mt-6 space-y-3">
            {["Low 5% platform fee — 3% for Pro organizers (+ Stripe processing fee)", "No hidden fees — transparent pricing, always", "Flexible fundraising — goal and deadline optional", "Guest donations with email receipts", "Controlled payouts through Stripe"]
              .map((x) => (
                <li key={x} className="flex items-center gap-2.5 text-sm text-ff-dark">
                  <ShieldCheck className="h-5 w-5 shrink-0 text-ff-green" /> {x}
                </li>
              ))}
          </ul>
        </div>

        <div className="lg:col-span-6">
          {user ? (
            <form onSubmit={create} className="rounded-2xl border border-ff-border bg-white p-8 shadow-sm" data-testid="start-fundraiser-form">
              <h2 className="font-heading text-xl font-bold text-ff-blue">Let's begin</h2>
              {error && <Alert variant="destructive" className="mt-4"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}
              <div className="mt-5 space-y-4">
                <div>
                  <Label htmlFor="title">Campaign title</Label>
                  <Input id="title" required data-testid="sf-title" value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5"
                    placeholder="Help Maria beat cancer" />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="sf-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="goal">Fundraising goal (optional)</Label>
                  <Input id="goal" type="number" min="0" data-testid="sf-goal" value={form.goal_amount}
                    onChange={(e) => setForm({ ...form, goal_amount: e.target.value })} className="mt-1.5" placeholder="e.g. 25000" />
                </div>
                <label className="flex cursor-pointer items-center gap-2.5 text-sm text-ff-dark">
                  <Checkbox data-testid="sf-nonprofit" checked={form.is_nonprofit}
                    onCheckedChange={(v) => setForm({ ...form, is_nonprofit: !!v })} />
                  This is a nonprofit campaign
                </label>
                <label className="flex cursor-pointer items-center gap-2.5 text-sm text-ff-dark">
                  <Checkbox data-testid="sf-team" checked={form.is_team}
                    onCheckedChange={(v) => setForm({ ...form, is_team: !!v })} />
                  This is a team fundraiser (multiple members + leaderboard)
                </label>
              </div>
              <Button type="submit" disabled={loading} data-testid="sf-submit"
                className="mt-6 w-full bg-ff-action font-semibold text-white hover:bg-ff-actionDark">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating</> : <><Rocket className="mr-2 h-4 w-4" /> Create & continue</>}
              </Button>
            </form>
          ) : (
            <div className="rounded-2xl border border-ff-border bg-white p-8 text-center shadow-sm" data-testid="start-fundraiser-signin">
              <h2 className="font-heading text-xl font-bold text-ff-blue">Create an organizer account</h2>
              <p className="mt-2 text-sm text-ff-dark">Organizer accounts are required to start a fundraiser. It's free.</p>
              <div className="mt-6 grid gap-3">
                <Button asChild className="bg-ff-action font-semibold text-white hover:bg-ff-actionDark">
                  <Link to="/sign-up" data-testid="sf-signup">Sign up to continue <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
                <Button asChild variant="outline" className="border-ff-blue text-ff-blue hover:bg-ff-blue hover:text-white">
                  <Link to="/sign-in" data-testid="sf-signin">I already have an account</Link>
                </Button>
              </div>
            </div>
          )}
        </div>
      </Container>
    </div>
  );
}
